import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, LogOut, DollarSign, Wallet, Receipt, TrendingUp,
  Banknote, Check, X, Building2, Landmark, Clock,
  LayoutDashboard, ReceiptText,
} from "lucide-react";
import TopBar from "@/components/TopBar";
import { useAuth } from "@/contexts/AuthContext";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import ExpensesPanel from "@/components/finance/ExpensesPanel";
import TreasuryPanel from "@/components/finance/TreasuryPanel";
import { PromptModal } from "@/components/AppDialogs";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";

const METHOD_LABEL = {
  mpesa: "M-Pesa",
  orange: "Orange Money",
  airtel: "Airtel Money",
  illicocash: "Illicocash",
  card: "Carte (Visa)",
  cash: "Cash",
};

const TABS = [
  { id: "overview",  label: "Vue d'ensemble", Icon: LayoutDashboard },
  { id: "treasury",  label: "Trésorerie",     Icon: Wallet },
  { id: "expenses",  label: "Dépenses",       Icon: ReceiptText },
];

export default function AdminFinance() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [tab, setTab] = useState("overview");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cashPending, setCashPending] = useState([]);
  const [cashLoading, setCashLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [confirmCashFor, setConfirmCashFor] = useState(null); // contribution
  const [rejectCashFor, setRejectCashFor] = useState(null); // contribution

  const isSuperAdmin = user?.role === "super_admin" || user?.role === "admin";

  const refresh = () => {
    setLoading(true);
    setCashLoading(true);
    api.get("/admin/finance/summary")
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
    api.get("/admin/contributions/awaiting-cash")
      .then((r) => setCashPending(r.data))
      .catch(() => setCashPending([]))
      .finally(() => setCashLoading(false));
  };

  useEffect(() => { refresh(); }, []);

  const confirmCash = (c) => setConfirmCashFor(c);
  const submitConfirmCash = async ({ channel, note }) => {
    const c = confirmCashFor;
    setConfirmCashFor(null);
    if (!c) return;
    setBusyId(c.id);
    try {
      await api.post(`/admin/contributions/${c.id}/confirm-cash`, {
        channel,
        note: (note || "").trim(),
      });
      refresh();
      toast.success("Paiement espèce confirmé.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec de la confirmation.");
    } finally { setBusyId(null); }
  };

  const rejectCash = (c) => setRejectCashFor(c);
  const submitRejectCash = async (reason) => {
    const c = rejectCashFor;
    setRejectCashFor(null);
    if (!c) return;
    setBusyId(c.id);
    try {
      await api.post(`/admin/contributions/${c.id}/reject-cash`, { reason });
      refresh();
      toast.success("Contribution rejetée.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Échec du rejet.");
    } finally { setBusyId(null); }
  };

  const handleLogout = async () => { await logout(); navigate("/"); };

  const currency = data?.currency || "USD";
  const fmt = (n) => `${currency === "USD" ? "$" : ""}${(n || 0).toLocaleString("fr-FR", { maximumFractionDigits: 0 })}${currency === "USD" ? "" : ` ${currency}`}`;

  return (
    <div className="min-h-screen bg-[#FFF9FB] pb-24" data-testid="admin-finance-page">
      <TopBar />
      <div className="max-w-6xl mx-auto px-5 pt-6">
        {isSuperAdmin && (
          <button
            onClick={() => navigate("/admin")}
            data-testid="finance-back"
            className="inline-flex items-center gap-2 text-sm text-[#5C4664] hover:text-[#2A0E3C] mb-4"
          >
            <ArrowLeft className="w-4 h-4" /> Tableau de bord
          </button>
        )}

        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#3FA535] mb-2">
              Trésorerie & Encaissements
            </p>
            <h1 className="font-display text-3xl sm:text-4xl font-black text-[#2A0E3C]">
              Espace Finances
            </h1>
            <p className="mt-2 text-sm text-[#5C4664]">
              Connecté en tant que <strong>{user?.email}</strong> — rôle <strong>{user?.role}</strong>
            </p>
          </div>
          <Button
            variant="outline"
            onClick={handleLogout}
            data-testid="finance-logout"
            className="rounded-full border-[#F2E2EC] gap-2"
          >
            <LogOut className="w-4 h-4" /> Déconnexion
          </Button>
        </div>

        {/* Tabs */}
        <nav data-testid="finance-tabs" className="mt-6 mb-5 flex gap-1.5 overflow-x-auto pb-1">
          {TABS.map(({ id, label, Icon }) => {
            const active = tab === id;
            return (
              <button
                key={id}
                onClick={() => setTab(id)}
                data-testid={`finance-tab-${id}`}
                className={`inline-flex items-center gap-2 px-4 h-10 rounded-full text-sm font-semibold whitespace-nowrap transition-colors ${
                  active
                    ? "bg-[#2A0E3C] text-white"
                    : "bg-white text-[#5C4664] border border-[#F2E2EC] hover:border-[#2A0E3C]/30"
                }`}
              >
                <Icon className="w-4 h-4" /> {label}
              </button>
            );
          })}
        </nav>

        {tab === "overview" && (
          <OverviewTab
            loading={loading}
            data={data}
            fmt={fmt}
            cashPending={cashPending}
            cashLoading={cashLoading}
            busyId={busyId}
            onConfirm={confirmCash}
            onReject={rejectCash}
          />
        )}
        {tab === "treasury" && <TreasuryPanel canMutate={isSuperAdmin} fmt={fmt} />}
        {tab === "expenses" && <ExpensesPanel canApprove={isSuperAdmin} fmt={fmt} />}
      </div>

      <ConfirmCashModal
        open={!!confirmCashFor}
        contribution={confirmCashFor}
        onClose={() => setConfirmCashFor(null)}
        onSubmit={submitConfirmCash}
      />
      <PromptModal
        open={!!rejectCashFor}
        onClose={() => setRejectCashFor(null)}
        onSubmit={submitRejectCash}
        title={rejectCashFor ? `Rejeter le paiement de ${rejectCashFor.contributor_name}` : ""}
        description={rejectCashFor ? `Code ${rejectCashFor.cash_code} — indiquez la raison du rejet (audit).` : ""}
        placeholder="Ex: montant non versé, invité injoignable, etc."
        kind="textarea"
        submitLabel="Rejeter la contribution"
        testId="reject-cash-modal"
      />
    </div>
  );
}

function ConfirmCashModal({ open, contribution, onClose, onSubmit }) {
  const [channel, setChannel] = useState("office");
  const [note, setNote] = useState("");
  useEffect(() => {
    if (open) { setChannel("office"); setNote(""); }
  }, [open]);
  if (!contribution) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent data-testid="confirm-cash-modal" className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display text-[#2A0E3C]">
            Confirmer le paiement espèce
          </DialogTitle>
          <DialogDescription className="text-[#5C4664]">
            {contribution.contributor_name} · Code {contribution.cash_code} · ${(contribution.amount || 0).toFixed(0)}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-2">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[#5C4664] mb-2">
              Canal de réception
            </p>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setChannel("office")}
                data-testid="confirm-cash-channel-office"
                className={`flex items-center gap-2 p-3 rounded-xl border-2 text-sm font-semibold transition-colors ${
                  channel === "office"
                    ? "border-[#D81B8C] bg-[#FCE7F3] text-[#D81B8C]"
                    : "border-[#F2E2EC] text-[#5C4664] hover:border-[#D81B8C]"
                }`}
              >
                <Building2 className="w-4 h-4" /> Bureau Milkado
              </button>
              <button
                type="button"
                onClick={() => setChannel("bank")}
                data-testid="confirm-cash-channel-bank"
                className={`flex items-center gap-2 p-3 rounded-xl border-2 text-sm font-semibold transition-colors ${
                  channel === "bank"
                    ? "border-[#D81B8C] bg-[#FCE7F3] text-[#D81B8C]"
                    : "border-[#F2E2EC] text-[#5C4664] hover:border-[#D81B8C]"
                }`}
              >
                <Landmark className="w-4 h-4" /> Dépôt bancaire
              </button>
            </div>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-[#5C4664] mb-2">
              Note (facultatif)
            </p>
            <Textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="N° de reçu, référence bancaire, etc."
              rows={3}
              data-testid="confirm-cash-note"
            />
          </div>
        </div>
        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-full" data-testid="confirm-cash-cancel">
            Annuler
          </Button>
          <Button
            onClick={() => onSubmit({ channel, note })}
            className="rounded-full bg-[#D81B8C] hover:bg-[#B21571] text-white"
            data-testid="confirm-cash-submit"
          >
            Confirmer la réception
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OverviewTab({ loading, data, fmt, cashPending, cashLoading, busyId, onConfirm, onReject }) {
  if (loading || !data) {
    return <p className="mt-6 text-sm text-[#5C4664]">Chargement des chiffres…</p>;
  }
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="finance-kpis">
        <Kpi
          Icon={DollarSign}
          label="Souscriptions"
          value={fmt(data.subscriptions.total)}
          hint={`${data.subscriptions.count} événement(s)`}
          accent="#D81B8C"
        />
        <Kpi
          Icon={Receipt}
          label="Cadeaux offerts"
          value={fmt(data.contributions.full_purchases.total)}
          hint={`${data.contributions.full_purchases.count} cadeau(x)`}
          accent="#6B2C8F"
        />
        <Kpi
          Icon={Wallet}
          label="Cotisations cagnottes"
          value={fmt(data.contributions.group_contributions.total)}
          hint="encaissées au total"
          accent="#FF8A00"
        />
        <Kpi
          Icon={TrendingUp}
          label="Cagnottes actives"
          value={fmt(data.active_cagnottes.collected)}
          hint={`Reste : ${fmt(data.active_cagnottes.remaining)}`}
          accent="#3FA535"
        />
      </div>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="finance-by-method">
        <MethodBreakdown
          title="Souscriptions par méthode"
          breakdown={data.subscriptions.by_method}
          fmt={fmt}
          empty="Aucune souscription encaissée pour le moment."
          testid="finance-subs-method"
        />
        <MethodBreakdown
          title="Contributions par méthode"
          breakdown={data.contributions.by_method}
          fmt={fmt}
          empty="Aucune contribution encaissée pour le moment."
          testid="finance-contrib-method"
        />
      </div>

      <CashPendingSection
        cashPending={cashPending}
        loading={cashLoading}
        busyId={busyId}
        onConfirm={onConfirm}
        onReject={onReject}
        fmt={fmt}
      />
    </>
  );
}

function Kpi({ Icon, label, value, hint, accent }) {
  return (
    <div className="bg-white rounded-2xl border border-[#F2E2EC] p-4">
      <div className="flex items-center justify-between">
        <p className="text-[10px] font-bold uppercase tracking-widest text-[#5C4664]">{label}</p>
        <div
          className="w-7 h-7 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: `${accent}15`, color: accent }}
        >
          <Icon className="w-3.5 h-3.5" />
        </div>
      </div>
      <p className="font-display text-2xl font-black mt-1" style={{ color: accent }}>{value}</p>
      {hint && <p className="text-[11px] text-[#5C4664] mt-1">{hint}</p>}
    </div>
  );
}

function MethodBreakdown({ title, breakdown, fmt, empty, testid }) {
  const entries = Object.entries(breakdown || {}).sort((a, b) => b[1] - a[1]);
  return (
    <div className="bg-white rounded-2xl border border-[#F2E2EC] p-5" data-testid={testid}>
      <h3 className="font-display text-lg font-black text-[#2A0E3C] mb-4">{title}</h3>
      {entries.length === 0 ? (
        <p className="text-sm text-[#5C4664]">{empty}</p>
      ) : (
        <ul className="space-y-2">
          {entries.map(([k, v]) => (
            <li key={k} className="flex items-center justify-between text-sm" data-testid={`${testid}-${k}`}>
              <span className="text-[#2A0E3C] font-semibold">{METHOD_LABEL[k] || k}</span>
              <span className="font-display font-black text-[#D81B8C]">{fmt(v)}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function formatRemaining(seconds) {
  if (seconds === null || seconds === undefined) return null;
  if (seconds <= 0) return "expiré";
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h >= 1) return `${h}h ${m.toString().padStart(2, "0")}min`;
  return `${m}min`;
}

function CashPendingSection({ cashPending, loading, busyId, onConfirm, onReject, fmt }) {
  return (
    <section
      data-testid="finance-cash-pending-section"
      className="mt-10 bg-white rounded-2xl border-2 border-[#3FA535]/40 p-5"
    >
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-xl bg-[#E8F6E5] text-[#3FA535] flex items-center justify-center">
            <Banknote className="w-4 h-4" />
          </div>
          <div>
            <h3 className="font-display text-lg font-black text-[#2A0E3C]">
              Paiements espèce en attente
            </h3>
            <p className="text-xs text-[#5C4664]">
              Confirmez la réception au bureau ou par dépôt bancaire pour libérer le pipeline logistique.
            </p>
          </div>
        </div>
        <span
          data-testid="finance-cash-pending-count"
          className="inline-flex items-center gap-1.5 px-3 h-7 rounded-full text-xs font-bold bg-[#FCE7F3] text-[#A60F6E]"
        >
          {cashPending.length} en attente
        </span>
      </div>

      {loading ? (
        <p className="text-sm text-[#5C4664]">Chargement…</p>
      ) : cashPending.length === 0 ? (
        <p data-testid="finance-cash-empty" className="text-sm text-[#5C4664] py-6 text-center">
          Aucun paiement en espèce à confirmer.
        </p>
      ) : (
        <ul className="space-y-3">
          {cashPending.map((c) => {
            const remaining = formatRemaining(c.seconds_until_expiry);
            const isExpired = c.is_expired;
            return (
              <li
                key={c.id}
                data-testid={`cash-pending-${c.id}`}
                className={`rounded-2xl border p-4 ${
                  isExpired ? "border-red-200 bg-red-50/50" : "border-[#F2E2EC] bg-[#FFF9FB]"
                }`}
              >
                <div className="flex items-start gap-4 flex-wrap">
                  <div className="flex-1 min-w-[200px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span
                        data-testid={`cash-pending-${c.id}-code`}
                        className="font-display text-xl font-black tracking-[0.25em] text-[#3FA535]"
                      >
                        {c.cash_code || "—"}
                      </span>
                      {isExpired && (
                        <span className="inline-flex items-center gap-1 px-2 h-5 rounded-full text-[10px] font-bold uppercase bg-red-100 text-red-700">
                          Expiré
                        </span>
                      )}
                    </div>
                    <p className="font-display font-black text-[#2A0E3C] mt-1">
                      {c.product_name || "Produit"}
                    </p>
                    <p className="text-xs text-[#5C4664]">
                      Événement : <strong>{c.event_title || c.event_code}</strong> ({c.event_code})
                    </p>
                    <p className="text-xs text-[#5C4664] mt-1">
                      Invité : <strong className="text-[#2A0E3C]">{c.contributor_name}</strong>
                      {c.contributor_phone && <> · <span className="font-mono">{c.contributor_phone}</span></>}
                    </p>
                    <p className="text-xs text-[#5C4664] mt-1 flex items-center gap-1.5">
                      <Clock className="w-3 h-3" />
                      {remaining === "expiré" ? (
                        <span className="text-red-700 font-semibold">Fenêtre dépassée</span>
                      ) : (
                        <>Temps restant : <strong>{remaining}</strong></>
                      )}
                    </p>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="font-display text-2xl font-black text-[#D81B8C]">
                      {fmt(c.amount)}
                    </div>
                    <div className="text-[10px] text-[#5C4664] uppercase tracking-widest mt-0.5">
                      à encaisser
                    </div>
                  </div>
                </div>

                <div className="mt-3 pt-3 border-t border-[#F2E2EC] flex flex-wrap gap-2">
                  <Button
                    onClick={() => onConfirm(c)}
                    disabled={busyId === c.id || isExpired}
                    data-testid={`cash-confirm-${c.id}`}
                    className="rounded-full h-9 px-4 text-sm font-semibold gap-2 bg-[#3FA535] hover:bg-[#2E7E27] text-white disabled:opacity-50"
                  >
                    <Check className="w-3.5 h-3.5" />
                    Confirmer l&apos;encaissement
                  </Button>
                  <Button
                    onClick={() => onReject(c)}
                    disabled={busyId === c.id}
                    variant="outline"
                    data-testid={`cash-reject-${c.id}`}
                    className="rounded-full h-9 px-4 text-sm font-semibold border-red-200 text-red-600 hover:bg-red-50 hover:border-red-400 gap-2 disabled:opacity-50"
                  >
                    <X className="w-3.5 h-3.5" />
                    Rejeter
                  </Button>
                  <div className="ml-auto flex items-center gap-3 text-[11px] text-[#5C4664]">
                    <span className="inline-flex items-center gap-1">
                      <Building2 className="w-3 h-3" /> Bureau
                    </span>
                    <span className="inline-flex items-center gap-1">
                      <Landmark className="w-3 h-3" /> Banque
                    </span>
                  </div>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
